import { ChangeEvent, ReactElement } from 'react';

import { AccessTextField } from '@changegps/horizon-component-library';
import { TextFieldProps } from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import {
  NumberFormatValues,
  PatternFormat,
  PatternFormatProps,
} from 'react-number-format';

export type PatternFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
} & Omit<PatternFormatProps, 'customInput' | 'onValueChange'> &
  Omit<TextFieldProps, 'InputProps'> & {
    InputProps?: Omit<TextFieldProps['InputProps'], 'inputRef'>;
    valueType: keyof NumberFormatValues;
  };

export const PatternField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  valueType = 'floatValue',
  ...props
}: PatternFieldProps<TFieldValues, TName>): ReactElement => {
  return (
    <Controller
      {...ControllerProps}
      render={({
        field: { ref, onChange, ...field },
        fieldState: { error },
      }) => (
        <PatternFormat
          {...field}
          {...props}
          error={props.error || Boolean(error)}
          helperText={error?.message || props.helperText}
          InputProps={{
            ...props.InputProps,
            inputRef: ref,
          }}
          customInput={AccessTextField}
          onValueChange={(values, { event }) => {
            let valueToSave:
              | NumberFormatValues['floatValue']
              | NumberFormatValues['value']
              | null;

            if (valueType === 'floatValue')
              valueToSave =
                typeof values.floatValue === 'undefined'
                  ? null
                  : values.floatValue;
            else valueToSave = values[valueType];

            onChange(valueToSave);

            props.onChange &&
              props.onChange(event as ChangeEvent<HTMLInputElement>);
          }}
        />
      )}
    />
  );
};
